import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Check, X, Users } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

/**
 * NewGroupChat — pick members from people you follow + name + create.
 * Routes to /groups/:id when created.
 */
export default function NewGroupChat() {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [name, setName] = useState('');
  const [q, setQ] = useState('');
  const [people, setPeople] = useState([]);
  const [selected, setSelected] = useState({}); // id -> user
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!me?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/users/${me.id}/following`);
        if (!cancelled) setPeople(data || []);
      } catch {}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [me?.id]);

  const filtered = q.trim()
    ? people.filter(p =>
        (p.display_name || '').toLowerCase().includes(q.toLowerCase()) ||
        (p.username || '').toLowerCase().includes(q.toLowerCase())
      )
    : people;

  const toggle = (u) => {
    setSelected(prev => {
      const next = { ...prev };
      if (next[u.id]) delete next[u.id];
      else next[u.id] = u;
      return next;
    });
  };

  const selectedList = Object.values(selected);

  const handleCreate = async () => {
    if (creating) return;
    if (selectedList.length < 1) {
      setError('Pick at least one member');
      return;
    }
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/groups', {
        name: name.trim() || `${selectedList[0]?.display_name?.split(' ')[0] || 'New'} group`,
        member_ids: selectedList.map(u => u.id),
      });
      navigate(`/groups/${data.id}`, { replace: true });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create group');
      setCreating(false);
    }
  };

  return (
    <div className="content-area animate-fade-in" data-testid="new-group-chat-page">
      <div className="sticky top-0 z-10 bg-[#FAFAF5] border-b border-black/[0.04] px-3 pt-[max(env(safe-area-inset-top),10px)] pb-3 flex items-center gap-2">
        <button
          onClick={() => navigate(-1)}
          className="w-10 h-10 rounded-full flex items-center justify-center active:bg-[#F1F0EA]"
          aria-label="Back"
          data-testid="new-group-chat-back"
        >
          <ArrowLeft size={20} className="text-[#111111]" />
        </button>
        <h1 className="flex-1 text-base font-bold text-[#111111]">New Group Chat</h1>
        <button
          onClick={handleCreate}
          disabled={creating || selectedList.length < 1}
          className="h-10 px-4 rounded-full bg-[#B6E600] text-[#111111] font-extrabold text-sm active:scale-95 disabled:opacity-40"
          data-testid="new-group-create"
        >
          {creating ? '…' : 'Create'}
        </button>
      </div>

      <div className="px-4 pt-3 space-y-3">
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={60}
          placeholder="Group name (optional)"
          className="w-full bg-white text-[#111111] placeholder-[#9B9EA3] rounded-2xl px-4 py-3 text-sm border border-black/[0.06] focus:outline-none focus:border-black/[0.18]"
          data-testid="new-group-name"
        />

        {selectedList.length > 0 && (
          <div className="flex flex-wrap gap-1.5" data-testid="new-group-selected">
            {selectedList.map(u => (
              <button
                key={u.id}
                onClick={() => toggle(u)}
                className="px-2.5 py-1 rounded-full bg-[#B6E600] text-[#111] text-xs font-bold flex items-center gap-1 active:scale-95"
              >
                {u.display_name} <X size={11} strokeWidth={3} />
              </button>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 bg-white rounded-2xl px-3 py-2.5 border border-black/[0.06]">
          <Search size={16} className="text-[#9B9EA3]" />
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search people you follow…"
            className="flex-1 bg-transparent text-sm text-[#111111] placeholder-[#9B9EA3] focus:outline-none"
            data-testid="new-group-search"
          />
        </div>

        {error && (
          <p className="text-xs text-[#FF3B30]" data-testid="new-group-error">{error}</p>
        )}
      </div>

      <div className="px-2 pt-3 pb-24">
        {loading ? (
          <p className="text-center text-xs text-[#9B9EA3] py-10">Loading people…</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 px-6">
            <div className="w-14 h-14 rounded-2xl bg-[#F1F0EA] flex items-center justify-center mx-auto mb-3">
              <Users size={22} className="text-[#9B9EA3]" />
            </div>
            <p className="text-sm font-semibold text-[#111111]">
              {q ? 'No matches' : 'Follow people to start a group'}
            </p>
            <p className="text-xs text-[#9B9EA3] mt-1">
              {q ? 'Try a different name' : 'You can only add people you already follow'}
            </p>
          </div>
        ) : (
          filtered.map(u => {
            const isSelected = !!selected[u.id];
            return (
              <button
                key={u.id}
                onClick={() => toggle(u)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-2xl active:bg-[#F1F0EA] ${isSelected ? 'bg-[#F1F0EA]' : ''}`}
                data-testid={`new-group-row-${u.username}`}
              >
                {u.profile_photo ? (
                  <img loading="lazy" decoding="async" src={u.profile_photo} alt="" className="w-11 h-11 rounded-full object-cover" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-[#E5E4DE]" />
                )}
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-semibold text-[#111111] truncate">{u.display_name}</p>
                  <p className="text-xs text-[#9B9EA3] truncate">@{u.username}</p>
                </div>
                <span
                  className={`w-6 h-6 rounded-full flex items-center justify-center border-2 ${isSelected ? 'bg-[#B6E600] border-[#B6E600]' : 'border-black/[0.15]'}`}
                  aria-hidden="true"
                >
                  {isSelected && <Check size={14} strokeWidth={3} className="text-[#111111]" />}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
